/**
 * Syncs the selected auth-status badge with Redux options and business-office context.
 *
 * @module useAuthStatusDropdown
 */
import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useBusniessOfficeNotesContext } from "../context/busniessOfficeNotesContext";
import { useDataAccumulatorForSingleOrder } from "../context/dataAccumulatorForSingleOrder";
import { RootState } from "../redux/store/store";

/**
 * useAuthStatusDropdown
 *
 * Custom hook that manages the selected Auth Status badge logic used across
 * multiple error components such as:
 * - InactiveInsurance
 * - RpaError
 * - StatusError
 *
 * This hook:
 * - Retrieves auth status options from the Redux store
 * - Determines the initial auth status based on the current order details
 * - Syncs the selected status with BusinessOfficeNotesContext
 * - Provides a handler to update the selected auth status
 *
 * @returns {object} Return object containing:
 * @returns {object|null} return.selectedAuthStatusBadge - The currently selected auth status badge object, or null if not found.
 * @returns {(badgeId: string) => void} return.handleAuthStatusChange - Function to update the selected auth status.
 * @returns {Array} return.authStatusOptions - List of all available auth status options.
 */

export const useAuthStatusDropdown = () => {
  const { data: authStatusOptions } = useSelector(
    (state: RootState) => state.authStatusOptions,
  );
  const { authOnFileDetails } = useDataAccumulatorForSingleOrder();
  const { manualAuthStatus, setManualAuthStatus } =
    useBusniessOfficeNotesContext();

  const initialAuthStatusBadge = useMemo(
    () =>
      authStatusOptions?.find(
        (option) => option.id === authOnFileDetails?.auth_on_file?.auth_status,
      ) || null,
    [authStatusOptions, authOnFileDetails?.auth_on_file?.auth_status],
  );

  const selectedAuthStatusBadge = useMemo(() => {
    if (manualAuthStatus && authStatusOptions) {
      return (
        authStatusOptions.find((option) => option.id === manualAuthStatus) ||
        null
      );
    }
    return initialAuthStatusBadge;
  }, [manualAuthStatus, authStatusOptions, initialAuthStatusBadge]);

  useEffect(() => {
    if (!manualAuthStatus && initialAuthStatusBadge?.id) {
      setManualAuthStatus(initialAuthStatusBadge.id);
    }
  }, [manualAuthStatus, initialAuthStatusBadge?.id]);

  const handleAuthStatusChange = (badgeId: string) => {
    setManualAuthStatus(badgeId);
  };

  return {
    selectedAuthStatusBadge,
    handleAuthStatusChange,
    authStatusOptions: authStatusOptions ?? [],
  };
};
